"use client";

import React from 'react';
import { usePathname } from 'next/navigation';
import { Header } from './Header';
import { Footer } from './Footer';
import { CookieConsent } from './CookieConsent';

interface PersistentLayoutProps {
  children: React.ReactNode;
}

/**
 * Persistent layout that doesn't remount on navigation
 * This prevents the header from jumping/reloading when switching pages
 *
 * /dashboard is a signed-in application, not a marketing page: it renders as a
 * standalone app shell with its own chrome (DashboardShell owns the sidebar and
 * top bar) and no marketing header/footer. Stacking the two meant the sidebar
 * competed with an 80px sticky header for the viewport and every dashboard page
 * carried the full site footer below the fold.
 *
 * usePathname is a client hook, so this stays compatible with the statically
 * prerendered marketing routes — nothing here reads headers()/cookies().
 *
 * There is deliberately NO app/(routes)/loading.tsx. A loading.tsx at the route
 * GROUP level wraps every marketing page in a Suspense boundary, and React then
 * streams the fallback first: <main> shipped containing nothing but a spinner
 * while the real <article> arrived after </footer>, to be moved into place by
 * client JS. That was the state of 70 of 71 pages. Crawlers and extractors that
 * do not run JS read <main> as "Loading...", and the document order put 71 pages
 * of identical footer boilerplate ahead of every page's unique content.
 *
 * /admin and /dashboard keep their own loading.tsx — those are the genuinely
 * slow signed-in routes the file was useful for. Do not reintroduce one at the
 * (routes) level to cover static marketing pages. Audit 2026-08-30.
 */
const PersistentLayout: React.FC<PersistentLayoutProps> = ({ children }) => {
  const pathname = usePathname();
  const isAppShell = pathname?.startsWith('/dashboard') ?? false;

  if (isAppShell) {
    return (
      <>
        {children}
        <CookieConsent />
      </>
    );
  }

  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
      <CookieConsent />
    </>
  );
};

export { PersistentLayout };
